import React, { useEffect, useLayoutEffect, useRef, useState, useContext } from "react";
import PropTypes from "prop-types";

import qahoy from "views_shared/app/javascript/scripts/qahoy";
import qapi from "views_shared/app/javascript/api";
import ListItem, { ListItemSkeleton, StubListItem } from "./list_item";
import useInfiniteScroll from "../../../hooks/use_infinite_scroll";
import { ProductContext } from "../../data/product_context_management";
import AddRepresentativeButton from "./add_representative_button";
import AddRepresentativeForm from "./add_representative_form";

// A stable identity for the omitted-prop case: the generation reset below compares `representatives` by
// reference, so a fresh `[]` default on every render would read as an endless stream of new sets and
// loop. Module-level constant keeps an absent prop equal to itself across renders.
const EMPTY_REPRESENTATIVES = [];

function PrRepresentativeList({
  representatives = EMPTY_REPRESENTATIVES,
  onAdd,
  onRemove,
  isEditable = false,
  onHydration = null,
}) {
  const perPage = 12;
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [profilesById, setProfilesById] = useState({});

  const [lastTriggeredAt, scrollTriggerRef] = useInfiniteScroll({ hasMore });

  const { asyncError, setAsyncError, isUpdating, viewerContext } = useContext(ProductContext);
  const editable = !!viewerContext?.editable;

  const [isEditing, setIsEditing] = useState(false);

  // A new representative SET (an add/remove hands down a fresh array, or the product reloads) starts a
  // fresh hydration generation: the profile map merged from the previous set is stale, and a filtered
  // representative would otherwise keep rendering under an old name with a remove control aimed at an
  // obsolete representation_id. Reset in render — before paint — so a changed set is never momentarily
  // judged "settled" against the prior read (which would flash a false stub for the just-added row until
  // the fetch effect re-ran). Only setState here (never a ref) so an abandoned/interrupted render rolls
  // the reset back with it. Page increments from infinite scroll keep the same array, so they merge
  // rather than reset. See the provider's matching hydration reset.
  const [lastRepresentatives, setLastRepresentatives] = useState(representatives);
  if (representatives !== lastRepresentatives) {
    setLastRepresentatives(representatives);
    setProfilesById({});
    setPage(1);
    setHasMore(false);
    setIsLoading(true);
  }

  // The latest COMMITTED representative set, so an in-flight read can tell whether its set is still
  // current. Written from a LAYOUT effect, not render and not a passive effect: layout effects run
  // synchronously at commit (before paint, before any resolved-promise microtask), so a set change is
  // reflected here the instant it commits — closing the window where a superseded read could resolve
  // after the new set committed but before a passive effect flushed and slip its stale writes through.
  // It still never runs for an abandoned render, so the ref can't outrun what actually committed. The
  // set's array identity is the generation: the provider mints a new array only when the underlying
  // representatives change, and page increments reuse the same array.
  const committedRepresentativesRef = useRef(representatives);
  useLayoutEffect(() => {
    committedRepresentativesRef.current = representatives;
  }, [representatives]);

  // Effect to track infinite scrolling intersection observer event, which increments page
  useEffect(() => {
    if (lastTriggeredAt > 0) setPage((prevPage) => prevPage + 1);
  }, [lastTriggeredAt]);

  // Retrieve a list of Profiles from the API and track the page number
  useEffect(() => {
    if (representatives === undefined) return;

    const profileIds = representatives.map((rep) => rep.profile_id);
    const representativesByProfileId = representatives.reduce((acc, representative) => {
      acc[representative.profile_id] = representative;
      return acc;
    }, {});

    // Fence this read to the set it was issued for. A read from a superseded set (started before an
    // add/remove or reload) would otherwise resolve later and merge a stale profile — with its obsolete
    // representation_id — into the map, or flip isLoading/hasMore for a set that no longer exists,
    // reopening the stale-row and false-settle races the render-phase reset closes for committed state.
    // Comparing against the committed set (not effect teardown) means later pages of the SAME set still
    // land and merge; only a set change invalidates a pending read.
    const isCurrent = () => committedRepresentativesRef.current === representatives;

    const fetchPrProfiles = async () => {
      try {
        if (page === 1) setIsLoading(true);

        if (profileIds.length === 0) {
          if (isCurrent()) setHasMore(false);
          return;
        }

        qahoy.track("PrRepresentativeList#fetchPrProfiles");

        const params = {
          page,
          per_page: perPage,
        };

        const response = await qapi.prProfiles.getAllById(profileIds, params);
        if (!isCurrent()) return;
        // getResource RESOLVES a non-2xx as `{ error }` rather than rejecting. Without this the
        // failure would fall straight through to the settle below and render as "no
        // representatives" — a read that failed must never be presented as a product that has none.
        if (!response.data) throw new Error(JSON.stringify(response.error));

        const profileResults = response.data;
        if (profileResults.length > 0) {
          const transformedProfiles = profileResults.map((item) => ({
            id: item.id,
            name: item.name,
            workplace_name: item.workplace_name,
            profile_image_url: item.profile_image_url,
            slug: item.slug,
            title: item.title,
            representation_id: representativesByProfileId[item.id].representation_id,
          }));

          setProfilesById((previousHash) => {
            return {
              ...previousHash,
              ...transformedProfiles.reduce((acc, profile) => {
                acc[profile.id] = profile;
                return acc;
              }, {}),
            };
          });
        }
        // getAllById does not actually paginate (it hands page/per_page to readUrlObjectBased at a key
        // it ignores), so the id-filtered read returns the whole bounded representative set in one
        // response and remainingRows is 0 — this stays false and the merge/settle logic below never sees
        // a second page today. Anyone wiring real pagination in must revisit how concurrent pages settle
        // isLoading/hasMore before trusting it.
        setHasMore(response.remainingRows > 0);
      } catch (error) {
        if (isCurrent()) setAsyncError(error);
      } finally {
        // Settle on the response, not on the hash becoming non-empty: pr_profiles applies
        // profile-visibility policies, so a legitimate response can contain nothing at all.
        if (isCurrent()) setIsLoading(false);
      }
    };
    fetchPrProfiles();
    // profilesById intentionally omitted from deps - it's modified by this effect,
    // including it would cause an infinite loop
  }, [representatives, page, perPage, setAsyncError]);

  // Let top-level ErrorBoundary see any async errors
  if (asyncError) throw asyncError;

  // The representative IDs come from the product view, which aggregates them without applying
  // profile-visibility policies; the pr_profiles hydration read does apply them, so it can legally
  // return fewer profiles than were asked for. The hydrated set is the authority on what this viewer
  // is allowed to see — rendering the raw ID list instead would leave a permanent skeleton row in
  // place of every profile the viewer may not see.
  const visibleRepresentatives = representatives?.filter((rep) => profilesById[rep.profile_id]) ?? [];

  // Report the hydration outcome upward: only this component knows how many representatives the
  // viewer may actually see, and the page needs that to decide whether an all-filtered list is
  // still worth a card (and a rail column). "Settled" means every page has landed — while more
  // pages are outstanding a zero count is not yet an answer.
  const hydrationSettled = !isLoading && !hasMore;
  const visibleCount = visibleRepresentatives.length;

  // The complement of the hydrated set: representations the pr_profiles read did not return — either an
  // RLS/visibility filter dropped them or their profile type is one pr_profiles never serves. A viewer
  // who cannot manage them keeps today's behaviour (they stay invisible); an editor gets a manageable
  // stub instead, but ONLY once hydration has settled — while a read is still outstanding an unresolved
  // profile is not yet proof of an unhydratable one, so no stub is rendered mid-hydration.
  const unhydratedRepresentatives = representatives?.filter((rep) => !profilesById[rep.profile_id]) ?? [];
  useEffect(() => {
    if (onHydration) onHydration({ settled: hydrationSettled, visibleCount });
  }, [onHydration, hydrationSettled, visibleCount]);

  // Only this component knows the full ordering of a heterogeneous list — placeholder, hydrated rows,
  // editor-only stubs, scroll trigger — so the divider between rows is computed here, across the two
  // row groups together, and never inside a row. Stubs stay last: that is what makes their positional
  // label count within its own group.
  const hydratedRows = isLoading ? [] : visibleRepresentatives;
  const stubRows = isEditable && hydrationSettled ? unhydratedRepresentatives : [];
  const rowCount = hydratedRows.length + stubRows.length;
  const rowClassName = (index) => `py-2${index < rowCount - 1 ? " border-bottom" : ""}`;

  return (
    <>
      <ul className="list-unstyled mb-0" aria-label="Representatives List">
        {isLoading && representatives?.length > 0 && (
          <li className="py-2">
            <ListItemSkeleton />
          </li>
        )}
        {hydratedRows.map((representative, index) => (
          <li key={representative.profile_id} className={rowClassName(index)}>
            <ListItem profile={profilesById[representative.profile_id]} onRemove={onRemove} isEditable={isEditable} />
          </li>
        ))}
        {stubRows.map((representative, index) => (
          <li key={representative.representation_id} className={rowClassName(hydratedRows.length + index)}>
            <StubListItem
              representationId={representative.representation_id}
              onRemove={onRemove}
              ordinal={index + 1}
              total={stubRows.length}
            />
          </li>
        ))}
      </ul>
      {/* The trigger is not a row, so it opens its own list rather than continuing the one above —
          an <li> in there would hang an empty divider under the last real row. */}
      {hasMore && (
        <div ref={scrollTriggerRef}>
          <ul className="list-unstyled mb-0">
            <li className="py-2">
              <ListItemSkeleton />
            </li>
          </ul>
        </div>
      )}
      {editable && !isEditing && <AddRepresentativeButton isLoading={isLoading} onClick={() => setIsEditing(true)} />}
      {editable && isEditing && (
        <AddRepresentativeForm
          onAdd={onAdd}
          onAsyncError={setAsyncError}
          isUpdating={isUpdating}
          setIsEditing={setIsEditing}
        />
      )}
    </>
  );
}

PrRepresentativeList.propTypes = {
  onRemove: PropTypes.func,
  onHydration: PropTypes.func,
  isEditable: PropTypes.bool,
  representatives: PropTypes.arrayOf(
    PropTypes.shape({
      profile_id: PropTypes.number,
      representation_id: PropTypes.number,
    }),
  ),
};

export default PrRepresentativeList;
